/*
 * WhataHoot booking page (experiences and event tickets). Same file uploaded
 * as assets/wah-experience-booking.js and assets/wah-event-ticket-booking.js.
 *
 * 1. Choose a date (sold-out dates can still be chosen to join the waitlist).
 * 2. Choose how many of each ticket / choice with + and − (e.g. 2 Vegetarian,
 *    1 Meat). All the choices on one date share its places.
 * 3. A row per person: name, email and, when the product requires it
 *    (tag "collect-dietary"), allergies / dietary needs (required; "None" is fine).
 * 4. Everything goes into the basket in one go, one cart line per choice.
 *    The events app reads "Attendee N name / email / allergies" on each line.
 *
 * Sold-out date: a "Join the waitlist" form posts to the events app
 * (section setting "Waitlist URL").
 */
// Styles for the parts added here (kept with the script so only this file changes).
const WAH_BOOKING_CSS = `
.wah-booking-counter { cursor: default; align-items: center; }
.wah-booking-counter.is-chosen {
  border-color: var(--wah-orange, #e37b3d);
  background: color-mix(in srgb, var(--wah-orange, #e37b3d) 10%, var(--wah-white, #fff));
  box-shadow: inset 0 0 0 1px var(--wah-orange, #e37b3d);
}
.wah-booking-counter.is-sold-out { opacity: .55; }
.wah-booking-counter__controls { grid-column: 2; grid-row: 1 / 3; display: flex; align-items: center; gap: .6rem; }
.wah-booking-counter__controls button {
  width: 4.4rem; height: 4.4rem; border: 1px solid rgba(var(--wah-black-rgb, 31, 38, 35), .25); border-radius: 50%;
  background: var(--wah-white, #fff); color: inherit; font-size: 2rem; font-weight: 900; line-height: 1; cursor: pointer;
}
.wah-booking-counter__controls button:disabled { opacity: .35; cursor: not-allowed; }
.wah-booking-counter__controls output { min-width: 2.4rem; text-align: center; font-size: 1.9rem; font-weight: 900; }
.wah-booking-counter__note { margin: .4rem 0 0; font-size: 1.25rem; opacity: .72; }
.wah-booking-person { padding: 1.4rem; border: 1px solid rgba(var(--wah-black-rgb, 31, 38, 35), .14); border-radius: 7px; background: var(--wah-white, #fff); }
.wah-booking-person__title { margin: 0 0 .4rem; font-weight: 900; }
.wah-booking-person__title span { margin-left: .6rem; font-weight: 400; opacity: .72; }
.wah-booking-waitlist { padding: 2.4rem; border-bottom: 1px solid rgba(var(--wah-orange-rgb, 227, 123, 61), .18); }
.wah-booking-waitlist__title { margin: 0 0 .6rem; font-size: 1.8rem; font-weight: 900; }
.wah-booking-waitlist__fields { display: grid; gap: 1rem; margin: 1.4rem 0; }
.wah-booking-waitlist__fields label { display: grid; gap: .4rem; font-size: 1.25rem; font-weight: 900; }
.wah-booking-waitlist__fields input {
  width: 100%; min-height: 4.8rem; padding: 1.2rem 1.4rem; border: 1px solid rgba(var(--wah-black-rgb, 31, 38, 35), .22);
  border-radius: 7px; background: var(--wah-cream, #fff8f1); color: inherit; font: inherit;
}
.wah-booking-waitlist__hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.wah-booking-waitlist__message { margin: 1.2rem 0 0; font-weight: 900; }
.wah-booking-waitlist__message:empty { display: none; }
.wah-booking-error { margin: 1.6rem 2.4rem 0; padding: 1.2rem 1.4rem; border-left: 4px solid #a03a20; background: rgba(160, 58, 32, .08); color: #a03a20; font-weight: 900; }
@media screen and (max-width: 749px) {
  .wah-booking-counter { grid-template-columns: minmax(0, 1fr) auto; }
  .wah-booking-counter__controls { grid-column: 2; grid-row: 1 / 3; }
}
`;

document.addEventListener('DOMContentLoaded', () => {
  if (!document.getElementById('wah-booking-additions')) {
    const style = document.createElement('style');
    style.id = 'wah-booking-additions';
    style.textContent = WAH_BOOKING_CSS;
    document.head.appendChild(style);
  }
  document.querySelectorAll('[data-experience-booking]').forEach((root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';

    const $ = (sel) => root.querySelector(sel);
    const form = $('[data-experience-form]');
    const variantsNode = $('[data-experience-variants]');
    const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
    const dateGrid = $('[data-experience-date-grid]');
    const dateSelect = $('[data-experience-date-select]');
    const singleDate = $('[data-experience-single-date]');
    const dateFieldset = $('[data-experience-date-fieldset]');
    const ticketGrid = $('[data-experience-ticket-grid]');
    const ticketFieldset = ticketGrid?.closest('fieldset');
    const quantityInput = $('[data-experience-quantity]');
    const price = $('[data-experience-price]');
    const submit = $('[data-experience-submit]');
    const submitTotal = $('[data-experience-submit-total]');
    const submitLabel = $('[data-experience-submit-label]');
    const attendeeFieldset = $('[data-attendee-email-fieldset]');
    const attendeeList = $('[data-attendee-email-list]');
    const attendeeSummary = $('[data-attendee-email-summary]');

    const dateIndex = Number(root.dataset.dateIndex);
    const ticketIndex = Number(root.dataset.ticketIndex);
    const hasDateOption = Number.isInteger(dateIndex) && dateIndex >= 0;
    const datePickerStyle = root.dataset.datePickerStyle || 'select';
    const dateTileLimit = Number(root.dataset.dateTileLimit || 8);
    const availabilityUnit = root.dataset.availabilityUnit || 'spaces';
    const soldOutText = root.dataset.soldOutText || 'Sold out';
    const availableText = root.dataset.availableText || 'left';
    const buttonLabel = root.dataset.buttonLabel || 'Add to basket';
    const maxPerOrder = Number(root.dataset.maximumQuantity || 20);
    const moneyFormat = window.money_format || window.theme?.moneyFormat;
    const emailsRequired = root.dataset.attendeeEmailsRequired === 'true';
    const attendeeDateProperty = root.dataset.attendeeDateProperty || 'experience date';
    // Set by the events app on the product (tags): "collect-dietary" shows an
    // allergies box per person; "events-app" means sold-out dates take a
    // waitlist. Data attributes on the section override both.
    const APP_WAITLIST_URL = 'https://whatahoot-events-management.vercel.app/api/waitlist';
    let collectDietary = root.dataset.collectDietary === 'true';
    let waitlistUrl = root.dataset.waitlistUrl || '';

    let activeDate = '';
    const counts = new Map(); // variant id -> how many

    // ------------------------------------------------------------ helpers

    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney && moneyFormat) return window.Shopify.formatMoney(cents, moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: window.Shopify?.currency?.active || 'GBP' }).format(Number(cents || 0) / 100);
    };
    const availabilityClass = (amount) => {
      if (amount <= 0) return 'wah-experience-booking__availability is-sold-out';
      if (amount <= 4) return 'wah-experience-booking__availability is-low';
      return 'wah-experience-booking__availability';
    };
    const availabilityLabel = (amount) => {
      if (amount > 0) return `${amount} ${availabilityUnit} ${availableText}`;
      return waitlistUrl ? `${soldOutText}: join the waitlist` : soldOutText;
    };
    const variantDate = (variant) => (hasDateOption && variant?.options ? variant.options[dateIndex] : '');
    const displayDate = (date) => date.replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+-\s+/, '');
    const isoDate = (date) => {
      const match = date.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2}))?/);
      return match ? `${match[1]}${match[2] ? `T${match[2]}:00` : ''}` : date;
    };
    const variantTicket = (variant) => {
      if (!variant?.options) return variant?.title || '';
      if (Number.isInteger(ticketIndex) && ticketIndex >= 0) return variant.options[ticketIndex];
      if (hasDateOption) return variant.options.filter((_, i) => i !== dateIndex).join(' · ') || variant.title;
      return variant.title;
    };
    // People on one ticket: "2 people sharing one still" = 2.
    const peoplePer = (variant) => {
      const label = [variant?.title, variantTicket(variant || {})].join(' ').toLowerCase();
      if (/\b2\s*(people|persons?|guests?|attendees?)\b/.test(label)) return 2;
      if (/\btwo\s*(people|persons?|guests?|attendees?)\b/.test(label)) return 2;
      if (/\b(shared|sharing|couple|pair)\b/.test(label)) return 2;
      return 1;
    };
    const variantsForDate = (date) => (hasDateOption ? variants.filter((v) => variantDate(v) === date) : variants);
    const dateAvailability = (date) => variantsForDate(date).reduce((max, v) => Math.max(max, Number(v.inventory_quantity || 0)), 0);
    const allDates = () => (hasDateOption ? [...new Set(variants.map(variantDate).filter(Boolean))] : []);
    const chosen = () => variantsForDate(activeDate).filter((v) => (counts.get(v.id) || 0) > 0);
    const ticketsChosen = () => chosen().reduce((n, v) => n + counts.get(v.id), 0);
    const placesLeft = () => (hasDateOption ? dateAvailability(activeDate) : Math.max(0, ...variants.map((v) => Number(v.inventory_quantity || 0))));
    const soldOut = () => placesLeft() <= 0;

    // ------------------------------------------------------------ layout

    // The old single-ticket quantity box is replaced by a counter per choice.
    quantityInput?.closest('fieldset')?.setAttribute('hidden', '');
    const renumber = () => {
      let n = 0;
      root.querySelectorAll('fieldset').forEach((f) => {
        const step = f.querySelector('.wah-experience-booking__step');
        if (step && !f.hidden) step.textContent = String(++n);
      });
    };

    const waitlistBox = document.createElement('div');
    waitlistBox.className = 'wah-booking-waitlist';
    waitlistBox.hidden = true;
    ticketFieldset?.after(waitlistBox);
    const errorBox = document.createElement('p');
    errorBox.className = 'wah-booking-error';
    errorBox.setAttribute('role', 'alert');
    errorBox.hidden = true;
    submit?.before(errorBox);

    // ------------------------------------------------------------ dates

    const pickDate = (date) => {
      activeDate = date;
      counts.clear();
      const first = variantsForDate(date).find((v) => v.available);
      if (first) counts.set(first.id, 1); // start with one of the first ticket
      render();
    };

    const renderDates = () => {
      if (!hasDateOption) { if (dateFieldset) dateFieldset.hidden = true; return; }
      const dates = allDates();
      if (dates.length <= 1) {
        activeDate = activeDate || dates[0] || '';
        if (dateSelect) { dateSelect.hidden = true; dateSelect.innerHTML = ''; }
        if (dateGrid) { dateGrid.hidden = true; dateGrid.innerHTML = ''; }
        if (singleDate) {
          const amount = dateAvailability(activeDate);
          singleDate.hidden = false;
          singleDate.innerHTML = activeDate
            ? `<strong>${esc(displayDate(activeDate))}</strong><span class="${availabilityClass(amount)}">${esc(availabilityLabel(amount))}</span>`
            : '<strong>Session confirmed at checkout</strong>';
        }
        return;
      }
      if (singleDate) { singleDate.hidden = true; singleDate.innerHTML = ''; }
      const useSelect = datePickerStyle === 'select' || (datePickerStyle === 'auto' && dates.length > dateTileLimit);
      if (dateSelect) { dateSelect.hidden = !useSelect; dateSelect.innerHTML = ''; }
      if (dateGrid) { dateGrid.hidden = useSelect; dateGrid.innerHTML = ''; }

      if (useSelect && dateSelect) {
        dates.forEach((date) => {
          const amount = dateAvailability(date);
          const option = document.createElement('option');
          option.value = date;
          option.disabled = amount <= 0 && !waitlistUrl;
          option.selected = date === activeDate;
          option.textContent = `${displayDate(date)} · ${availabilityLabel(amount)}`;
          dateSelect.appendChild(option);
        });
        return;
      }
      dates.forEach((date) => {
        const amount = dateAvailability(date);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wah-experience-booking__choice';
        button.disabled = amount <= 0 && !waitlistUrl;
        button.setAttribute('aria-pressed', String(date === activeDate));
        button.innerHTML = `
          <span>
            <span class="wah-experience-booking__choice-title">${esc(displayDate(date))}</span>
            <span class="wah-experience-booking__choice-meta">${esc(root.dataset.dateMeta || 'WhataHoot Distillery, King’s Lynn')}</span>
          </span>
          <span class="${availabilityClass(amount)}">${esc(availabilityLabel(amount))}</span>`;
        button.addEventListener('click', () => pickDate(date));
        dateGrid.appendChild(button);
      });
    };

    // ------------------------------------------------------------ choices (counters)

    const renderTickets = () => {
      if (!ticketGrid) return;
      ticketGrid.innerHTML = '';
      const left = placesLeft();
      const total = ticketsChosen();
      variantsForDate(activeDate).forEach((variant) => {
        const n = counts.get(variant.id) || 0;
        const canAdd = variant.available && total < Math.min(left, maxPerOrder) && n < Number(variant.inventory_quantity || 0);
        const row = document.createElement('div');
        row.className = `wah-experience-booking__choice wah-booking-counter${n ? ' is-chosen' : ''}${variant.available ? '' : ' is-sold-out'}`;
        row.innerHTML = `
          <span>
            <span class="wah-experience-booking__choice-title">${esc(variantTicket(variant))}</span>
            <span class="wah-experience-booking__choice-meta">${variant.available ? esc(formatMoney(variant.price)) : esc(soldOutText)}</span>
          </span>
          <span class="wah-booking-counter__controls">
            <button type="button" data-less aria-label="One fewer ${esc(variantTicket(variant))}" ${n ? '' : 'disabled'}>−</button>
            <output aria-live="polite" aria-label="${esc(variantTicket(variant))}: how many">${n}</output>
            <button type="button" data-more aria-label="One more ${esc(variantTicket(variant))}" ${canAdd ? '' : 'disabled'}>+</button>
          </span>`;
        row.querySelector('[data-less]').addEventListener('click', () => { counts.set(variant.id, Math.max(0, n - 1)); render(); });
        row.querySelector('[data-more]').addEventListener('click', () => { counts.set(variant.id, n + 1); render(); });
        ticketGrid.appendChild(row);
      });
      const note = document.createElement('p');
      note.className = 'wah-booking-counter__note';
      note.textContent = left > 0 ? `${total} chosen · ${left} ${availabilityUnit} ${availableText} on this date` : '';
      if (note.textContent) ticketGrid.appendChild(note);
    };

    // ------------------------------------------------------------ people

    // One row per person, grouped by ticket. Typed values survive re-renders.
    const typed = () => {
      const out = {};
      attendeeList?.querySelectorAll('input[data-key]').forEach((i) => { out[i.dataset.key] = i.value; });
      return out;
    };
    const renderPeople = () => {
      if (!attendeeList) return;
      const before = typed();
      attendeeList.innerHTML = '';
      let person = 0;
      chosen().forEach((variant) => {
        const people = counts.get(variant.id) * peoplePer(variant);
        for (let k = 1; k <= people; k += 1) {
          person += 1;
          const key = `${variant.id}-${k}`;
          const row = document.createElement('div');
          row.className = 'wah-experience-booking__attendee-row wah-booking-person';
          row.innerHTML = `
            <p class="wah-booking-person__title">Person ${person} <span>${esc(variantTicket(variant))}</span></p>
            <label for="${root.id}-n-${key}">Name</label>
            <input id="${root.id}-n-${key}" data-key="n-${key}" autocomplete="${person === 1 ? 'name' : 'off'}" required value="${esc(before[`n-${key}`] || '')}">
            <label for="${root.id}-e-${key}">Email${emailsRequired ? '' : ' (optional)'}</label>
            <input id="${root.id}-e-${key}" data-key="e-${key}" type="email" autocomplete="${person === 1 ? 'email' : 'off'}" ${emailsRequired ? 'required' : ''} value="${esc(before[`e-${key}`] || '')}">
            ${collectDietary ? `<label for="${root.id}-d-${key}">Allergies or dietary needs</label>
            <input id="${root.id}-d-${key}" data-key="d-${key}" required placeholder="e.g. nut allergy, vegetarian, or “None”" value="${esc(before[`d-${key}`] || '')}">` : ''}`;
          attendeeList.appendChild(row);
        }
      });
      if (attendeeFieldset) attendeeFieldset.hidden = person === 0;
      if (attendeeSummary) {
        attendeeSummary.textContent = person === 1
          ? `Who’s coming? We use the name on the ticket and the email for pre-arrival information${collectDietary ? '. Please tell us about any allergies or dietary needs (write “None” if there aren’t any)' : ''}.`
          : `Who’s coming? Add each of the ${person} people. We use the names on the tickets and the emails for pre-arrival information${collectDietary ? '. Please tell us about any allergies or dietary needs for each person (write “None” if there aren’t any)' : ''}.`;
      }
    };

    // ------------------------------------------------------------ totals

    const renderTotals = () => {
      const total = chosen().reduce((n, v) => n + v.price * counts.get(v.id), 0);
      const count = ticketsChosen();
      if (submitTotal) submitTotal.textContent = formatMoney(total);
      if (price) {
        const cheapest = variantsForDate(activeDate).filter((v) => v.available).map((v) => v.price);
        if (cheapest.length) price.textContent = formatMoney(Math.min(...cheapest));
      }
      if (submit) submit.disabled = soldOut() || count === 0;
      if (submitLabel) submitLabel.textContent = soldOut() ? soldOutText : count === 0 ? 'Choose your tickets' : buttonLabel;
    };

    // ------------------------------------------------------------ waitlist

    const renderWaitlist = () => {
      const full = soldOut() && Boolean(activeDate || !hasDateOption);
      waitlistBox.hidden = !(full && waitlistUrl);
      if (ticketFieldset) ticketFieldset.hidden = full && Boolean(waitlistUrl);
      if (attendeeFieldset && full) attendeeFieldset.hidden = true;
      if (submit) submit.hidden = full && Boolean(waitlistUrl);
      if (waitlistBox.hidden || waitlistBox.dataset.date === activeDate) return;
      waitlistBox.dataset.date = activeDate;
      waitlistBox.innerHTML = `
        <p class="wah-booking-waitlist__title">${esc(displayDate(activeDate) || 'This date')} is fully booked</p>
        <p>Join the waitlist and we’ll email you if places come up. You’ll get a link to book, with the places held for you for a limited time.</p>
        <div class="wah-booking-waitlist__fields">
          <label>Name <input data-w="name" autocomplete="name" required></label>
          <label>Email <input data-w="email" type="email" autocomplete="email" required></label>
          <label>How many people <input data-w="people" type="number" min="1" max="20" value="2" inputmode="numeric"></label>
          <label class="wah-booking-waitlist__hp" aria-hidden="true">Website <input data-w="website" tabindex="-1" autocomplete="off"></label>
        </div>
        <button type="button" class="wah-button" data-join>Join the waitlist</button>
        <p class="wah-booking-waitlist__message" role="status"></p>`;
      waitlistBox.querySelector('[data-join]').addEventListener('click', joinWaitlist);
    };

    const joinWaitlist = async () => {
      const get = (k) => waitlistBox.querySelector(`[data-w="${k}"]`);
      const message = waitlistBox.querySelector('.wah-booking-waitlist__message');
      const button = waitlistBox.querySelector('[data-join]');
      for (const k of ['name', 'email', 'people']) if (!get(k).reportValidity()) return;
      const variant = variantsForDate(activeDate)[0];
      button.disabled = true;
      message.textContent = 'Adding you to the waitlist…';
      try {
        const res = await fetch(waitlistUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant_id: variant?.id, name: get('name').value.trim(), email: get('email').value.trim(), people: Number(get('people').value) || 1, website: get('website').value }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Sorry, that didn’t work. Please try again or contact us.');
        waitlistBox.querySelector('.wah-booking-waitlist__fields').hidden = true;
        button.hidden = true;
        message.textContent = body.position
          ? `You’re on the waitlist (number ${body.position}). We’ve sent you an email, and we’ll be in touch if places come up.`
          : 'You’re on the waitlist. We’ll be in touch if places come up.';
      } catch (err) {
        message.textContent = err.message;
        button.disabled = false;
      }
    };

    // ------------------------------------------------------------ render

    const render = () => {
      renderDates();
      renderTickets();
      renderPeople();
      renderTotals();
      renderWaitlist();
      renumber();
      errorBox.hidden = true;
    };

    dateSelect?.addEventListener('change', () => pickDate(dateSelect.value));

    // ------------------------------------------------------------ add to basket

    form?.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      if (!chosen().length) return;
      if (!form.reportValidity()) return;
      const values = typed();
      // Hidden fields the page already has (Session, Event date, …) go on every line.
      const base = {};
      form.querySelectorAll('input[type="hidden"][name^="properties["]').forEach((i) => { base[i.name.slice(11, -1)] = i.value; });
      const dateLabel = displayDate(activeDate);
      const items = chosen().map((variant) => {
        const properties = { ...base };
        for (const key of Object.keys(properties)) {
          if (/date iso$/i.test(key)) properties[key] = isoDate(activeDate);
          else if (/date$/i.test(key)) properties[key] = dateLabel;
          else if (/^session$/i.test(key)) properties[key] = activeDate;
          else if (/ticket type$/i.test(key)) properties[key] = variantTicket(variant);
          else if (/variant title$/i.test(key)) properties[key] = variant.title;
        }
        const people = counts.get(variant.id) * peoplePer(variant);
        const emails = [];
        for (let k = 1; k <= people; k += 1) {
          const key = `${variant.id}-${k}`;
          const name = (values[`n-${key}`] || '').trim();
          const email = (values[`e-${key}`] || '').trim();
          const diet = (values[`d-${key}`] || '').trim();
          if (name) properties[`Attendee ${k} name`] = name;
          if (email) { properties[`Attendee ${k} email`] = email; emails.push(email); }
          if (diet) properties[`Attendee ${k} allergies`] = diet;
          properties[`Attendee ${k} ${attendeeDateProperty}`] = dateLabel;
        }
        if (emails.length) properties['Attendee emails'] = emails.join(', ');
        properties['Attendee count'] = String(people);
        properties['Attendees per booking unit'] = String(peoplePer(variant));
        return { id: variant.id, quantity: counts.get(variant.id), properties };
      });

      submit.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Adding…';
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.description || body.message || 'Those tickets couldn’t be added. Please try again.');
        // A theme can open its cart drawer instead by defining window.wahAfterAddToCart.
        if (typeof window.wahAfterAddToCart === 'function') window.wahAfterAddToCart(items);
        else window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (err) {
        errorBox.textContent = err.message;
        errorBox.hidden = false;
        renderTotals();
      }
    });

    // ------------------------------------------------------------ start

    // "Attendee emails" → "Who's coming" (names, emails and allergies now).
    const legend = attendeeFieldset?.querySelector('legend');
    if (legend?.lastChild?.nodeType === Node.TEXT_NODE) legend.lastChild.textContent = 'Who’s coming';

    const productUrl = `${window.location.pathname.replace(/\/$/, '')}.js`;
    if (root.dataset.collectDietary === undefined || root.dataset.waitlistUrl === undefined) {
      fetch(productUrl, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((product) => {
          const tags = (product?.tags || []).map((t) => String(t).toLowerCase());
          if (root.dataset.collectDietary === undefined) collectDietary = tags.includes('collect-dietary');
          if (root.dataset.waitlistUrl === undefined && tags.includes('events-app')) waitlistUrl = APP_WAITLIST_URL;
          if (collectDietary || waitlistUrl) render();
        })
        .catch(() => { /* booking still works without these */ });
    }

    const params = new URL(window.location.href).searchParams;
    const fromUrl = variants.find((v) => String(v.id) === params.get('variant'));
    const start = fromUrl || variants.find((v) => v.available) || variants[0];
    if (hasDateOption) {
      pickDate(start ? variantDate(start) : allDates()[0] || '');
      if (fromUrl?.available) { counts.clear(); counts.set(fromUrl.id, 1); render(); }
    }
    else { if (start?.available) counts.set(start.id, 1); render(); }
  });
});